<script setup lang="ts">
// Page V2 « Clients » — liste paginée des clients (companies actives) côté serveur,
// avec recherche CLIENT-ONLY (pas l'omnisearch global), tri par colonne et pagination.
// Sert aussi de cible de drill-down depuis la recherche globale : /clients?q=…
import { onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
} from '@heroicons/vue/24/outline'
import { useClientsListStore } from '@/v2/stores/useClientsListStore'
import type { ClientListItem, ClientSortField, ClientStatusFilter } from '@/api/clientsApi'

const route = useRoute()
const router = useRouter()
const store = useClientsListStore()
const { q, page, pageCount, total, from, to, rows, loading, error, sort, order, status } =
  storeToRefs(store)

const COLUMNS: { field: ClientSortField; label: string; sortable: boolean }[] = [
  { field: 'name', label: 'Raison sociale', sortable: true },
  { field: 'siren', label: 'SIREN', sortable: true },
  { field: 'siret', label: 'SIRET', sortable: true },
  { field: 'zip', label: 'Code postal', sortable: true },
]

// Étiquette statut métier (currentStatus stocké en minuscule côté Odoo).
const STATUS_LABEL: Record<string, string> = {
  client: 'Client',
  prospect: 'Prospect',
  suspect: 'Suspect',
}
const STATUS_BADGE: Record<string, string> = {
  client: 'badge-success',
  prospect: 'badge-info',
  suspect: 'badge-ghost',
}
const STATUS_FILTERS: { value: ClientStatusFilter; label: string }[] = [
  { value: 'client', label: 'Clients' },
  { value: 'prospect', label: 'Prospects' },
  { value: 'suspect', label: 'Suspects' },
  { value: 'all', label: 'Tous' },
]

function onInput(e: Event) {
  store.setQuery((e.target as HTMLInputElement).value)
}

function clientHref(item: ClientListItem): string | null {
  // Fiche client 360° (uuid boost-suite), repli ancienne fiche partner Odoo.
  if (item.uuid) return `/client/${item.uuid}`
  if (item.referenceExterne) return `/partner/${item.referenceExterne}`
  return null
}

function goTo(item: ClientListItem) {
  const href = clientHref(item)
  if (href) router.push(href)
}

// Sync q ↔ URL (liens partageables + drill-down omnisearch)
watch(q, (value) => {
  router.replace({ query: { ...route.query, q: value || undefined } })
})

onMounted(() => {
  store.init((route.query.q as string | undefined) ?? '')
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header class="mb-6">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <BuildingOffice2Icon class="h-6 w-6 text-primary" aria-hidden="true" />
        Clients
      </h1>
      <p class="mt-1 text-base-content/70">Liste de vos clients — recherche par nom, SIREN, SIRET ou code postal.</p>
    </header>

    <!-- Barre de recherche client-only -->
    <div
      class="mb-4 flex items-center gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-2.5 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
    >
      <MagnifyingGlassIcon
        class="h-5 w-5 shrink-0"
        :class="loading ? 'text-primary' : 'text-base-content/40'"
        aria-hidden="true"
      />
      <input
        type="text"
        class="flex-1 bg-transparent text-base text-base-content outline-none placeholder:text-base-content/30"
        placeholder="Rechercher un client (nom, SIREN, SIRET, code postal)…"
        :value="q"
        aria-label="Rechercher un client"
        @input="onInput"
      />
      <ArrowPathIcon v-if="loading" class="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      <button
        v-if="q"
        type="button"
        class="flex h-6 w-6 items-center justify-center rounded-full text-base-content/40 transition-colors hover:bg-base-200 hover:text-base-content"
        aria-label="Effacer la recherche"
        @click="store.setQuery('')"
      >
        <XMarkIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- Filtre statut — masqué pendant une recherche (qui porte sur tous les statuts) -->
    <div v-if="!q" class="mb-4 flex flex-wrap items-center gap-1.5">
      <button
        v-for="opt in STATUS_FILTERS"
        :key="opt.value"
        type="button"
        class="rounded-full border px-3 py-1 text-sm font-medium transition-colors"
        :class="
          status === opt.value
            ? 'border-primary bg-primary text-primary-content'
            : 'border-base-300 bg-base-100 text-base-content/60 hover:border-primary/50 hover:text-base-content'
        "
        @click="store.setStatus(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>
    <p v-else class="mb-4 text-sm text-base-content/50">Recherche sur tous les statuts.</p>

    <!-- Erreur -->
    <div v-if="error" role="alert" class="alert alert-error">
      <span>Une erreur est survenue lors du chargement des clients.</span>
      <button type="button" class="btn btn-sm" @click="store.reload()">Réessayer</button>
    </div>

    <template v-else>
      <!-- Chargement initial -->
      <div
        v-if="loading && !rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center shadow-sm"
      >
        <span class="loading loading-spinner" aria-hidden="true"></span>
        <p class="mt-2 text-base-content/70">Chargement des clients…</p>
      </div>

      <!-- Aucun résultat -->
      <div
        v-else-if="!rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center text-base-content/70 shadow-sm"
      >
        <template v-if="q">Aucun client pour « {{ q }} ».</template>
        <template v-else>Aucun client.</template>
      </div>

      <template v-else>
        <!-- Tri (mobile uniquement — sur ≥ md le tri se fait via les en-têtes de colonne) -->
        <div class="mb-3 flex items-center gap-2 md:hidden">
          <label for="clients-mobile-sort" class="shrink-0 text-sm text-base-content/70">Trier :</label>
          <select
            id="clients-mobile-sort"
            class="select select-bordered flex-1 text-base"
            :value="sort"
            @change="store.setSort(($event.target as HTMLSelectElement).value as ClientSortField)"
          >
            <option v-for="col in COLUMNS" :key="col.field" :value="col.field">{{ col.label }}</option>
          </select>
          <button
            type="button"
            class="btn btn-square"
            :aria-label="
              order === 'asc'
                ? 'Ordre croissant — changer en décroissant'
                : 'Ordre décroissant — changer en croissant'
            "
            @click="store.setSort(sort)"
          >
            <ChevronUpIcon v-if="order === 'asc'" class="h-5 w-5" aria-hidden="true" />
            <ChevronDownIcon v-else class="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <!-- Tableau (tablette / desktop ≥ md) -->
        <div class="hidden overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm md:block">
          <table class="table">
            <thead>
              <tr>
                <th v-for="col in COLUMNS" :key="col.field" scope="col">
                  <button
                    v-if="col.sortable"
                    type="button"
                    class="flex items-center gap-1 font-semibold hover:text-primary"
                    @click="store.setSort(col.field)"
                  >
                    {{ col.label }}
                    <ChevronUpIcon v-if="sort === col.field && order === 'asc'" class="h-3.5 w-3.5" aria-hidden="true" />
                    <ChevronDownIcon v-else-if="sort === col.field && order === 'desc'" class="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <span v-else>{{ col.label }}</span>
                </th>
                <th scope="col">Statut</th>
                <th scope="col">TVA</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in rows"
                :key="item.uuid"
                class="hover"
                :class="clientHref(item) ? 'cursor-pointer' : ''"
                @click="goTo(item)"
              >
                <td class="font-medium">{{ item.name ?? '—' }}</td>
                <td class="tabular-nums">{{ item.siren ?? '—' }}</td>
                <td class="tabular-nums">{{ item.siret ?? '—' }}</td>
                <td class="tabular-nums">{{ item.zip ?? '—' }}</td>
                <td>
                  <span
                    v-if="item.currentStatus"
                    class="badge badge-sm"
                    :class="STATUS_BADGE[item.currentStatus.toLowerCase()] ?? 'badge-ghost'"
                  >
                    {{ STATUS_LABEL[item.currentStatus.toLowerCase()] ?? item.currentStatus }}
                  </span>
                  <span v-else class="text-base-content/40">—</span>
                </td>
                <td class="tabular-nums">{{ item.vat ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cartes (mobile < md) — le tableau dense est illisible sur petit écran -->
        <ul role="list" class="flex flex-col gap-3 md:hidden">
          <li v-for="item in rows" :key="item.uuid">
            <component
              :is="clientHref(item) ? 'button' : 'div'"
              :type="clientHref(item) ? 'button' : undefined"
              class="flex w-full flex-col gap-2 rounded-xl border border-base-200 bg-base-100 p-4 text-left shadow-sm transition-colors motion-reduce:transition-none"
              :class="
                clientHref(item)
                  ? 'cursor-pointer hover:border-primary/40 focus-visible:outline-2 focus-visible:outline-primary'
                  : ''
              "
              @click="goTo(item)"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="font-semibold text-base-content">{{ item.name ?? '—' }}</span>
                <span
                  v-if="item.currentStatus"
                  class="badge badge-sm shrink-0"
                  :class="STATUS_BADGE[item.currentStatus.toLowerCase()] ?? 'badge-ghost'"
                >
                  {{ STATUS_LABEL[item.currentStatus.toLowerCase()] ?? item.currentStatus }}
                </span>
              </div>
              <dl class="grid grid-cols-2 gap-x-3 gap-y-2">
                <div class="flex flex-col">
                  <dt class="text-xs font-medium uppercase tracking-wide text-base-content/70">SIREN</dt>
                  <dd class="tabular-nums text-base-content">{{ item.siren ?? '—' }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs font-medium uppercase tracking-wide text-base-content/70">SIRET</dt>
                  <dd class="tabular-nums text-base-content">{{ item.siret ?? '—' }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs font-medium uppercase tracking-wide text-base-content/70">Code postal</dt>
                  <dd class="tabular-nums text-base-content">{{ item.zip ?? '—' }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs font-medium uppercase tracking-wide text-base-content/70">TVA</dt>
                  <dd class="tabular-nums text-base-content">{{ item.vat ?? '—' }}</dd>
                </div>
              </dl>
            </component>
          </li>
        </ul>
      </template>
    </template>

    <!-- Pagination -->
    <div v-if="!error" class="mt-4 flex flex-wrap items-center justify-between gap-3">
      <span class="text-sm text-base-content/70">
        <template v-if="total > 0">{{ from }}–{{ to }} sur {{ total }} client{{ total > 1 ? 's' : '' }}</template>
        <template v-else>0 client</template>
      </span>
      <div class="join">
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page <= 1 || loading"
          aria-label="Page précédente"
          @click="store.setPage(page - 1)"
        >
          <ChevronLeftIcon class="h-4 w-4" aria-hidden="true" />
        </button>
        <span class="btn btn-sm join-item pointer-events-none">Page {{ page }} / {{ pageCount }}</span>
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page >= pageCount || loading"
          aria-label="Page suivante"
          @click="store.setPage(page + 1)"
        >
          <ChevronRightIcon class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
